import { useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Select from "react-select";
import { bouncy } from "ldrs";
import { toast } from "react-toastify";
import {
  IE_ISSUE_MESSAGES,
  formatIssueTypeInput,
  getIeIssueValidationSchema,
  mapIeIssueBackendErrors,
} from "./ieIssueFormValidation";

bouncy.register();

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const CreateIeIssueModal = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
  existingIssues = [],
  kpiTypeOptions = [],
  onSave,
  isSaving = false,
}) => {
  const kpiOptions = useMemo(() => {
    const options = (kpiTypeOptions ?? []).map((item) => ({
      id: item.id ?? item.kpitypeId ?? item.kpiTypeId,
      name: item.name ?? item.kpitype ?? item.kpiTypeName ?? "",
    }));

    if (!isEditMode || !editRow?.kpiTypeId) {
      return options;
    }

    const selectedId = String(editRow.kpiTypeId);
    const hasSelected = options.some((item) => String(item.id) === selectedId);

    if (hasSelected) {
      return options;
    }

    return [
      ...options,
      {
        id: editRow.kpiTypeId,
        name: editRow.kpiTypeName || "N/A",
      },
    ];
  }, [kpiTypeOptions, isEditMode, editRow]);

  const handleClose = () => {
    setShowModal(false);
  };

  const initialValues = {
    issueType: isEditMode ? editRow?.issueType ?? "" : "",
    kpiTypeId: isEditMode
      ? String(editRow?.kpiTypeId ?? "")
      : "",
    isActive: isEditMode
      ? editRow?.isActive === false
        ? "false"
        : "true"
      : "true",
  };

  const validationSchema = useMemo(
    () =>
      getIeIssueValidationSchema({
        existingIssues,
        isEditMode,
        editRow,
      }),
    [existingIssues, isEditMode, editRow]
  );

  const handleSubmit = async (values, { setFieldError, setSubmitting }) => {
    const payload = {
      issueType: values.issueType.trim(),
      kpiTypeId: Number(values.kpiTypeId),
      isActive: values.isActive === "true",
    };

    if (isEditMode) {
      payload.ieIssueId =
        editRow?.ieIssueId ??
        editRow?.ieIssueTypeId ??
        editRow?.issueTypeId ??
        editRow?.id;
    }

    if (!onSave) {
      setSubmitting(false);
      return;
    }

    try {
      const result = await onSave(payload, isEditMode);

      if (result?.success !== false) {
        toast.success(
          isEditMode
            ? IE_ISSUE_MESSAGES.updateSuccess
            : IE_ISSUE_MESSAGES.createSuccess
        );
        handleClose();
        return;
      }

      const fieldErrors =
        result?.fieldErrors ??
        mapIeIssueBackendErrors(result, result?.message);

      if (Object.keys(fieldErrors).length) {
        Object.entries(fieldErrors).forEach(([field, message]) => {
          if (message) setFieldError(field, message);
        });
      } else {
        toast.error(result?.message || IE_ISSUE_MESSAGES.saveFailed);
      }
    } catch (error) {
      const responseData = error?.response?.data;
      const message =
        typeof responseData === "string"
          ? responseData
          : responseData?.message || IE_ISSUE_MESSAGES.saveFailed;
      const fieldErrors = mapIeIssueBackendErrors(responseData, message);

      if (Object.keys(fieldErrors).length) {
        Object.entries(fieldErrors).forEach(([field, msg]) => {
          if (msg) setFieldError(field, msg);
        });
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const modalTitle = isEditMode ? "Edit IE Issue Type" : "Add IE Issue Type";

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="md"
      aria-labelledby="ie-issue-modal-title"
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="ie-issue-modal-title">{modalTitle}</Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          key={isEditMode ? editRow?.ieIssueId ?? editRow?.id : "new"}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label htmlFor="issueType" className="form-label">
                    Issue Type <span className="text-danger">*</span>
                  </label>
                  <Field
                    type="text"
                    name="issueType"
                    id="issueType"
                    placeholder="Enter Issue Type"
                    className="form-control"
                    maxLength={100}
                    onChange={(e) => {
                      setFieldValue(
                        "issueType",
                        formatIssueTypeInput(e.target.value)
                      );
                    }}
                  />
                  <ErrorMessage
                    className="small required-message"
                    name="issueType"
                    component="div"
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label htmlFor="kpiTypeId" className="form-label">
                    KPI Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    inputId="kpiTypeId"
                    name="kpiTypeId"
                    classNamePrefix="react-select"
                    options={kpiOptions.map((kpiType) => ({
                      value: String(kpiType.id),
                      label: kpiType.name,
                    }))}
                    value={
                      kpiOptions
                        .map((kpiType) => ({
                          value: String(kpiType.id),
                          label: kpiType.name,
                        }))
                        .find((option) => option.value === String(values.kpiTypeId)) ||
                      null
                    }
                    onChange={(selected) =>
                      setFieldValue("kpiTypeId", selected ? selected.value : "")
                    }
                    placeholder="Select KPI Type"
                    isClearable={false}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                  <ErrorMessage
                    className="small required-message"
                    name="kpiTypeId"
                    component="div"
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-center gap-4">
                    <div className="form-check mb-0">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="activeRadio"
                        value="true"
                      />
                      <label className="form-check-label" htmlFor="activeRadio">
                        Active
                      </label>
                    </div>
                    <div className="form-check mb-0">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="inactiveRadio"
                        value="false"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="inactiveRadio"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    className="small required-message"
                    name="isActive"
                    component="div"
                  />
                </div>
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                  disabled={isSubmitting || isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={saveButtonStyle}
                  disabled={isSubmitting || isSaving}
                >
                  {isSubmitting || isSaving ? (
                    <l-bouncy size="25" speed="1.75" color="white"></l-bouncy>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default CreateIeIssueModal;
